import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-hot-toast';
import { Input, Textarea, CustomSelect, ConfirmModal, DialogShell, DialogHeader, DialogFooter, dialogButtonClass, FormFieldWithError, Amount } from '@redpanda/ui';
import { createPayroll, updatePayroll, previewPayroll } from '../services/api';
import { Payroll, usePayrollStoreBase } from '../stores/payrollStore';
import { useEmployeeOptionsStore, employeeOptions } from '../stores/employeeStore';
import { notifyDataChanged } from '../utils/dataEvents';
import { apiErrorMessage } from '../utils/apiError';
import { removeListItem } from '../utils/pendingItem';
import { monthOptions, yearOptions } from '../utils/period';
import { useNumberInput, useUnsavedChangesWarning } from '@redpanda/utils';

interface PayrollDialogProps {
  payroll?: Payroll;
  onClose: () => void;
}

const INCOME_FIELDS = ['baseSalary', 'allowances', 'bonuses', 'overtimePay'] as const;
const DEDUCTION_FIELDS = ['laborInsurance', 'healthInsurance', 'laborPension', 'otherDeductions'] as const;
// 公司負擔（不從薪資扣、不影響實領；報表的人力成本用）
const EMPLOYER_FIELDS = ['employerLaborInsurance', 'employerHealthInsurance', 'employerPension'] as const;
const ALL_FIELDS = [...INCOME_FIELDS, ...DEDUCTION_FIELDS, ...EMPLOYER_FIELDS];
type AmountField = (typeof ALL_FIELDS)[number];
const PAYROLL_STATUSES = ['DRAFT', 'CONFIRMED', 'PAID'];

type FormData = Record<AmountField, string> & {
  employeeId: string;
  periodYear: string;
  periodMonth: string;
  status: string;
  notes: string;
};

const emptyForm = (): FormData => {
  const now = new Date();
  return {
    employeeId: '', periodYear: String(now.getFullYear()), periodMonth: String(now.getMonth() + 1), status: 'DRAFT', notes: '',
    baseSalary: '', allowances: '', bonuses: '', overtimePay: '', laborInsurance: '', healthInsurance: '', laborPension: '', otherDeductions: '',
    employerLaborInsurance: '', employerHealthInsurance: '', employerPension: '',
  };
};

const sum = (data: Partial<FormData>, fields: readonly AmountField[]) => fields.reduce((total, f) => total + (Number(data[f]) || 0), 0);

/** 新增／編輯薪資：樂觀更新＋立即關閉；實領由後端依同一個公式重算 */
const PayrollDialog: React.FC<PayrollDialogProps> = ({ payroll, onClose }) => {
  const { t } = useTranslation(['pages', 'translation']);
  const isEditMode = !!payroll;
  // 已發放的薪資只能改狀態（後端同規則）
  const locked = payroll?.status === 'PAID';
  const { employees, fetchAll } = useEmployeeOptionsStore();
  const amountInput = useNumberInput('decimal');
  const { register, handleSubmit, formState: { errors, isDirty }, setValue, reset, watch } = useForm<FormData>({ defaultValues: emptyForm() });

  useEffect(() => { fetchAll(); }, [fetchAll]);

  useLayoutEffect(() => {
    if (!payroll) { reset(emptyForm()); return; }
    const amounts = Object.fromEntries(ALL_FIELDS.map(f => [f, String(payroll[f] ?? 0)])) as Record<AmountField, string>;
    reset({
      ...amounts,
      employeeId: payroll.employeeId,
      periodYear: String(payroll.periodYear),
      periodMonth: String(payroll.periodMonth),
      status: payroll.status,
      notes: payroll.notes || '',
    });
  }, [payroll, reset]);

  const { handleClose, showConfirm, handleConfirmClose, handleCancelClose } = useUnsavedChangesWarning(isDirty, onClose);

  const values = watch();

  // 新增時：選了員工與月份就依薪資設定試算並帶入（一般企業做法：系統先算，HR 再調整獎金、扣款）
  // 使用者自己改過金額後就不再覆蓋
  const [preview, setPreview] = useState<'idle' | 'filled' | 'noSetting'>('idle');
  const amountsTouched = useRef(false);
  useEffect(() => {
    if (payroll || !values.employeeId || amountsTouched.current) return;
    let cancelled = false;
    previewPayroll(values.employeeId, Number(values.periodYear), Number(values.periodMonth))
      .then(({ data }) => {
        if (cancelled || amountsTouched.current) return;
        if (!data.amounts) { setPreview('noSetting'); return; }
        ALL_FIELDS.forEach(f => setValue(f, String(data.amounts[f] ?? 0), { shouldDirty: true }));
        setPreview('filled');
      })
      .catch(() => { if (!cancelled) setPreview('idle'); });
    return () => { cancelled = true; };
  }, [payroll, values.employeeId, values.periodYear, values.periodMonth, setValue]);
  const income = sum(values, INCOME_FIELDS);
  const deductions = sum(values, DEDUCTION_FIELDS);

  const onSubmit = (data: FormData) => {
    const amounts = Object.fromEntries(ALL_FIELDS.map(f => [f, Number(data[f]) || 0])) as Record<AmountField, number>;
    const payload = locked ? { status: data.status } : {
      ...amounts,
      employeeId: data.employeeId,
      periodYear: Number(data.periodYear),
      periodMonth: Number(data.periodMonth),
      status: data.status,
      notes: data.notes.trim(),
    };
    const selected = employees.find(e => e.id === data.employeeId);
    const draft = {
      ...payload,
      netSalary: sum(data, INCOME_FIELDS) - sum(data, DEDUCTION_FIELDS),
      employee: selected ? { id: selected.id, name: selected.name, department: selected.department } : payroll?.employee,
    };
    const store = usePayrollStoreBase.getState();
    onClose();

    if (payroll) {
      const original = { ...payroll };
      store.updateItem(payroll.id, locked ? { status: data.status } : draft);
      updatePayroll(payroll.id, payload)
        .then(response => {
          usePayrollStoreBase.getState().updateItem(payroll.id, response.data);
          toast.success(t('pages:payroll.edit.success'));
          notifyDataChanged('payrolls');
        })
        .catch(error => {
          usePayrollStoreBase.getState().updateItem(payroll.id, original);
          toast.error(apiErrorMessage(error, 'translation:common.messages.updateFailed'));
        });
      return;
    }

    const tempId = `temp-${Date.now()}`;
    store.addItem({ ...(draft as Payroll), id: tempId });
    createPayroll(payload)
      .then(response => {
        usePayrollStoreBase.getState().updateItem(tempId, response.data);
        toast.success(t('pages:payroll.add.success'));
        notifyDataChanged('payrolls');
      })
      .catch(error => {
        removeListItem(usePayrollStoreBase, tempId);
        toast.error(apiErrorMessage(error, 'translation:common.messages.createFailed'));
      });
  };

  const amountField = (field: AmountField, required = false) => (
    <FormFieldWithError key={field} label={t(`pages:payroll.form.${field}`)} required={required} error={errors[field]?.message} htmlFor={field}>
      <Input
        id={field}
        inputMode="decimal"
        className="text-right tabular-nums"
        disabled={locked}
        onInput={() => { amountsTouched.current = true; }}
        {...amountInput.bind(register(field, {
          ...(required && { required: t('pages:payroll.form.enterBaseSalary') }),
          pattern: { value: /^\d*(\.\d{0,2})?$/, message: t('pages:payroll.form.invalidAmount') },
        }))}
        variant={errors[field] ? 'error' : 'default'}
        placeholder="0"
      />
    </FormFieldWithError>
  );

  return (
    <>
      <DialogShell onClose={handleClose} className="max-w-2xl">
        <DialogHeader
          title={isEditMode ? t('pages:payroll.edit.title') : t('pages:payroll.add.title')}
          onClose={handleClose}
          closeLabel={t('translation:common.actions.close')}
        />
        <form onSubmit={handleSubmit(onSubmit)}>
          <div className="p-6 space-y-6">
            {locked && <p className="text-sm text-fg-secondary">{t('pages:payroll.form.paidLocked')}</p>}

            <div className="grid grid-cols-1 sm:grid-cols-4 gap-x-4 gap-y-5">
              <div className="sm:col-span-2">
                <FormFieldWithError label={t('pages:payroll.form.employee')} required error={errors.employeeId?.message} htmlFor="employeeId">
                  <input type="hidden" {...register('employeeId', { required: t('translation:common.forms.validation.required') })} />
                  <CustomSelect
                    id="employeeId"
                    value={values.employeeId}
                    onChange={value => setValue('employeeId', value, { shouldDirty: true, shouldValidate: true })}
                    variant={errors.employeeId ? 'error' : 'default'}
                    disabled={locked}
                    searchable
                    placeholder={t('pages:payroll.form.selectEmployee')}
                    options={employeeOptions(employees, payroll?.employeeId)}
                  />
                </FormFieldWithError>
              </div>
              <FormFieldWithError label={t('pages:payroll.form.year')} required htmlFor="periodYear">
                <CustomSelect id="periodYear" value={values.periodYear} disabled={locked} onChange={value => setValue('periodYear', value, { shouldDirty: true })} options={yearOptions(4, 1)} />
              </FormFieldWithError>
              <FormFieldWithError label={t('pages:payroll.form.month')} required htmlFor="periodMonth">
                <CustomSelect id="periodMonth" value={values.periodMonth} disabled={locked} onChange={value => setValue('periodMonth', value, { shouldDirty: true })} options={monthOptions(t)} />
              </FormFieldWithError>
            </div>

            {preview !== 'idle' && !locked && (
              <p className={`text-[13px] ${preview === 'noSetting' ? 'text-red-600 dark:text-red-400' : 'text-fg-secondary'}`}>
                {t(preview === 'filled' ? 'pages:payroll.form.previewFilled' : 'pages:payroll.form.previewNoSetting')}
              </p>
            )}

            <section>
              <h3 className="text-base font-semibold text-fg mb-3">{t('pages:payroll.form.incomeSection')}</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-5">
                {INCOME_FIELDS.map(field => amountField(field, field === 'baseSalary'))}
              </div>
            </section>

            <section>
              <h3 className="text-base font-semibold text-fg mb-3">{t('pages:payroll.form.deductionsSection')}</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-5">
                {DEDUCTION_FIELDS.map(field => amountField(field))}
              </div>
            </section>

            {/* 實領：收入－扣除，一行摘要（不用彩色色塊） */}
            <dl className="rounded-md border border-line divide-y divide-line text-sm">
              <div className="flex justify-between px-4 py-2"><dt className="text-fg-secondary">{t('pages:payroll.form.totalIncome')}</dt><dd className="tabular-nums"><Amount value={income} /></dd></div>
              <div className="flex justify-between px-4 py-2"><dt className="text-fg-secondary">{t('pages:payroll.form.totalDeductions')}</dt><dd className="tabular-nums"><Amount value={-deductions} /></dd></div>
              <div className="flex justify-between px-4 py-2.5 font-semibold text-fg"><dt>{t('pages:payroll.form.netSalary')}</dt><dd className="tabular-nums"><Amount value={income - deductions} /></dd></div>
            </dl>

            <section>
              <h3 className="text-base font-semibold text-fg mb-1">{t('pages:payroll.form.employerSection')}</h3>
              <p className="text-[13px] text-fg-secondary mb-3">{t('pages:payroll.form.employerHint')}</p>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-x-4 gap-y-5">
                {EMPLOYER_FIELDS.map(field => amountField(field))}
              </div>
            </section>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-5">
              <FormFieldWithError label={t('pages:payroll.form.status')} required htmlFor="status">
                <CustomSelect
                  id="status"
                  value={values.status}
                  onChange={value => setValue('status', value, { shouldDirty: true })}
                  options={PAYROLL_STATUSES.map(value => ({ value, label: t(`pages:payroll.status.${value}`) }))}
                />
              </FormFieldWithError>
              <div className="sm:col-span-2">
                <FormFieldWithError label={t('pages:payroll.form.notes')} error={errors.notes?.message} htmlFor="notes">
                  <Textarea
                    id="notes"
                    rows={2}
                    disabled={locked}
                    {...register('notes', { maxLength: { value: 1000, message: t('pages:payroll.form.notesMaxLength') } })}
                    variant={errors.notes ? 'error' : 'default'}
                  />
                </FormFieldWithError>
              </div>
            </div>
          </div>
          <DialogFooter>
            <button type="button" onClick={handleClose} className={dialogButtonClass.cancel}>
              {t('translation:common.actions.cancel')}
            </button>
            <button type="submit" className={dialogButtonClass.primary}>
              {isEditMode ? t('translation:common.actions.save') : t('translation:common.actions.create')}
            </button>
          </DialogFooter>
        </form>
      </DialogShell>

      <ConfirmModal
        isOpen={showConfirm}
        onClose={handleCancelClose}
        onConfirm={handleConfirmClose}
        title={t('translation:components.modal.unsavedChanges.title')}
        message={t('translation:components.modal.unsavedChanges.message')}
        confirmText={t('translation:components.modal.unsavedChanges.confirmButton')}
        cancelText={t('translation:components.modal.unsavedChanges.cancelButton')}
        t={t}
      />
    </>
  );
};

export default PayrollDialog;
